import { type KeyboardEvent, useMemo, useRef, useState } from 'react'
import { LockSimpleIcon, MapPinIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import { Input } from 'app/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { closeModal, showModal } from 'app/lib/modal'
import { nodeGates } from 'app/lib/workflows/blockGates'
import type { BlockedGate, GateLocation, GateToggles } from 'app/types/workflow-gates'
import type { WorkflowNode } from 'app/types/workflows'
import { pmsDisplayName } from 'lib/pmsNames'
import { LockPanel } from './LockPanel'

type Blocker = { gate: BlockedGate; node: WorkflowNode }

/** Past this many locations the list gets a search box; below it, scanning is quicker than typing. */
const SEARCH_FROM = 8

/** The first step this location can't run, ignoring unreleased ones: those are the same everywhere,
 *  so they say nothing about which location to pick. */
function blockerAt(location: GateLocation, nodes: WorkflowNode[], toggles: GateToggles): Blocker | undefined {
  for (const [nodeId, gate] of nodeGates(nodes, location, toggles)) {
    if (gate.state === `hidden` && gate.why === `toggle`) continue
    const node = nodes.find((candidate) => candidate.id === nodeId)
    if (node) return { gate, node }
  }
  return undefined
}

type LocationChooserProps = {
  locations: GateLocation[]
  nodes: WorkflowNode[]
  toggles: GateToggles
  currentLocationId?: string
  onChoose: (locationId: string) => void
  onCancel: () => void
}

/** Asked the moment a workflow is created, before the canvas: a workflow runs at one location, and
 *  what it may use depends on what that location has. Locations it can run at come first; the ones
 *  that can't run one of its steps follow under their own heading, padlocked and unselectable. */
export function showLocationChooser(props: LocationChooserProps) {
  // `closeModal` calls this modal's `onClose`, so a choice and a close must settle once.
  let settled = false
  const done = (fn: () => void) => {
    if (settled) return
    settled = true
    closeModal()
    fn()
  }
  showModal(
    <LocationChooser
      {...props}
      onChoose={(id) => done(() => props.onChoose(id))}
      onCancel={() => done(props.onCancel)}
    />,
    {
      useRadix: true,
      isFullScreen: false,
      title: `Which location is this workflow for?`,
      removePadding: true,
      overflowHidden: true,
      panelClassName: `min-w-0 sm:max-w-md`,
      onClose: () => done(props.onCancel),
    },
  )
}

export function LocationChooser({
  locations,
  nodes,
  toggles,
  currentLocationId,
  onChoose,
  onCancel,
}: LocationChooserProps) {
  const [query, setQuery] = useState(``)
  const list = useRef<HTMLUListElement>(null)
  const searchable = locations.length > SEARCH_FROM
  const { open: choosable, locked } = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const rows = locations
      .map((location) => ({ location, name: location.name || location.id }))
      .filter(({ name }) => terms.every((term) => name.toLowerCase().includes(term)))
      .map((row) => ({ ...row, blocker: blockerAt(row.location, nodes, toggles) }))
    return {
      open: rows.filter((row) => !row.blocker).sort((a, b) => a.name.localeCompare(b.name)),
      locked: rows.filter((row) => row.blocker).sort((a, b) => a.name.localeCompare(b.name)),
    }
  }, [locations, nodes, toggles, query])

  // Arrow keys walk the rows (search box included) by moving real focus, so Enter picks the focused
  // row through the button's own click.
  const step = (event: KeyboardEvent) => {
    if (event.key !== `ArrowDown` && event.key !== `ArrowUp`) return
    const rows = [...(list.current?.querySelectorAll<HTMLButtonElement>(`[data-location-row]`) ?? [])]
    if (rows.length === 0) return
    event.preventDefault()
    const at = rows.indexOf(document.activeElement as HTMLButtonElement)
    const next = event.key === `ArrowDown` ? Math.min(at + 1, rows.length - 1) : Math.max(at - 1, 0)
    rows[at === -1 ? 0 : next].focus()
  }

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-5">
        <p className="text-sm text-subtle">Texts send from its number and run on its clock.</p>
        {searchable && (
          <Input
            autoFocus
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={step}
            placeholder="Search locations"
            aria-label="Search locations"
          />
        )}
        <ul ref={list} onKeyDown={step} className="flex max-h-80 flex-col gap-1 overflow-y-auto">
          {choosable.length + locked.length === 0 && (
            <li className="py-4 text-center text-xs text-subtle">No location matches “{query.trim()}”.</li>
          )}
          {choosable.map(({ location, name }) => (
            <li key={location.id}>
              <Button
                variant="ghost"
                size="row"
                className="w-full"
                data-location-row
                data-testid={`location-choice-${location.id}`}
                aria-current={location.id === currentLocationId || undefined}
                onClick={() => onChoose(location.id)}
              >
                <MapPinIcon size={16} weight="bold" className="shrink-0 text-subtle" />
                <span className="truncate text-sm text-default">{name}</span>
                {location.id === currentLocationId && (
                  <span className="ml-auto shrink-0 text-xs text-subtle">Current</span>
                )}
              </Button>
            </li>
          ))}
          {locked.length > 0 && (
            <li className="px-2 pt-3 pb-1 text-xs font-semibold text-subtle">Can't run this workflow</li>
          )}
          {locked.map(({ location, name, blocker }) => {
            const { gate, node } = blocker!
            const label = node.data.label ?? node.type
            const reason =
              gate.state === `locked`
                ? gate.why === `product`
                  ? `Needs ${gate.product}`
                  : `Needs your practice system connected`
                : `${pmsDisplayName(location.pms, `Its practice system`)} can't run ${label}`
            const row = (
              <Button
                variant="ghost"
                size="row"
                className="w-full"
                data-location-row
                data-testid={`location-choice-locked-${location.id}`}
              >
                <LockSimpleIcon size={16} weight="bold" className="shrink-0 text-subtle" />
                <span className="truncate text-sm text-subtle">{name}</span>
                <span className="ml-auto shrink-0 text-xs text-subtle">{reason}</span>
              </Button>
            )
            return (
              <li key={location.id}>
                {gate.state === `locked` ? (
                  <Popover>
                    <PopoverTrigger asChild>{row}</PopoverTrigger>
                    <PopoverContent align="end" className="w-64">
                      <LockPanel
                        gate={gate}
                        blockLabel={label}
                        blockKind={node.type === `trigger` ? node.data.kind : node.type}
                        location={location}
                      />
                    </PopoverContent>
                  </Popover>
                ) : (
                  row
                )}
              </li>
            )
          })}
        </ul>
      </div>
      <DialogActionBar>
        <Button variant="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
      </DialogActionBar>
    </>
  )
}
